import { applyAnalyticsConsent, productAnalyticsConfigured, startAnalytics, track, type Surface } from './analytics'
import { mountPrivacyUi } from './banner'
import { getConsent, onConsentChange, watchOtherTabs } from './consent'

/**
 * Runs first on every page (vite.config.ts injects it into each HTML entry).
 * Nothing optional starts until the stored choice says so.
 */

function surfaceFor(pathname: string): Surface {
  if (pathname.startsWith('/app')) return 'editor'
  if (pathname.startsWith('/builder')) return 'builder'
  return 'site'
}

const surface = surfaceFor(window.location.pathname)
const initial = getConsent()

removeOldAdTraces()
startAnalytics(surface, initial.choices)
watchOtherTabs()
onConsentChange(({ choices }) => applyAnalyticsConsent(choices))

mountPrivacyUi({ analytics: productAnalyticsConfigured || import.meta.env.DEV }, (choices, via) =>
  track('privacy_choice_made', { analytics: choices.analytics, via }),
)

if (surface === 'site') trackCtaClicks()

/**
 * EasyPatt used the ChatGPT Ads pixel until October 2026. Its cookies live
 * up to a year, so browsers that visited before keep them; nothing reads
 * them any more, and they go on the next visit. Safe to delete after
 * November 2027.
 */
function removeOldAdTraces(): void {
  try {
    const cookies = document.cookie
    const parentDomain = window.location.hostname.split('.').slice(-2).join('.')
    for (const name of ['__oppref', '__obref']) {
      if (!new RegExp(`(?:^|;\\s*)${name}=`).test(cookies)) continue
      document.cookie = `${name}=; Max-Age=0; Path=/`
      document.cookie = `${name}=; Max-Age=0; Path=/; Domain=.${parentDomain}`
    }
    const keys: string[] = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i)
      if (k?.startsWith('easypatt.signupReported.')) keys.push(k)
    }
    for (const k of keys) window.localStorage.removeItem(k)
  } catch {
    // Storage blocked: nothing to clean.
  }
}

/** Which button on a marketing page sent people into the app or the builder. */
function trackCtaClicks(): void {
  document.addEventListener(
    'click',
    (e) => {
      const link = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!link) return
      let url: URL
      try {
        url = new URL(link.href, window.location.href)
      } catch {
        return
      }
      if (url.origin !== window.location.origin) return
      const target = url.pathname.startsWith('/app') ? 'app' : url.pathname.startsWith('/builder') ? 'builder' : null
      if (!target) return
      track('cta_clicked', { target, placement: placementOf(link) })
    },
    { capture: true },
  )
}

function placementOf(link: HTMLAnchorElement): string {
  if (link.dataset.cta) return link.dataset.cta
  const landmark = link.closest('header, nav, footer')
  if (landmark) return landmark.tagName.toLowerCase()
  const section = link.closest('[id]')
  return section?.id || 'body'
}
